import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type OverlayStackDepthContextValue = {
  // Claims the next depth value, above anything currently open, and returns it.
  // Call once per overlay (modal, drawer, popover, ...) open, not on every render -
  // calling it on every render will keep incrementing forever.
  claimOverlayDepth: () => number;
  // Releases a previously claimed depth. Call once the overlay closes/unmounts.
  releaseOverlayDepth: () => void;
};

// Shared depth counter for the whole overlay stack (0 = nothing open).
// Lets modals, drawers, and popovers offset their z-index so more recently opened
// overlays render on top, regardless of whether they're route-driven or nested inside
// another overlay.
export const OverlayStackDepthContext =
  React.createContext<OverlayStackDepthContextValue>({
    claimOverlayDepth: () => 0,
    releaseOverlayDepth: () => {},
  });

export const OverlayStackDepthProvider = ({ children }: PropsWithChildren<unknown>) => {
  const depthRef = useRef(0);

  const claimOverlayDepth = useCallback(() => {
    depthRef.current += 1;
    return depthRef.current;
  }, []);

  const releaseOverlayDepth = useCallback(() => {
    depthRef.current = Math.max(depthRef.current - 1, 0);
  }, []);

  return (
    <OverlayStackDepthContext.Provider value={{ claimOverlayDepth, releaseOverlayDepth }}>
      {children}
    </OverlayStackDepthContext.Provider>
  );
};

// Claims a depth slot for the duration an overlay is visible, and releases it once it
// closes/unmounts. Shared by modals, drawers, and popovers so they can offset their
// z-index without duplicating this claim/release effect in each component.
export const useOverlayStackDepth = (visible: boolean): number | null => {
  const { claimOverlayDepth, releaseOverlayDepth } = useContext(OverlayStackDepthContext);
  const [stackDepth, setStackDepth] = useState<number | null>(null);

  useEffect(() => {
    if (!visible) return;

    setStackDepth(claimOverlayDepth());

    return () => releaseOverlayDepth();
  }, [visible, claimOverlayDepth, releaseOverlayDepth]);

  return stackDepth;
};
